import React, { PropsWithChildren, createContext, useContext } from 'react';
import { Notification } from '../types/notificationTypes';
import useSWR, { KeyedMutator } from 'swr';
import { useClient } from '../hooks/useClient';
import { useAuthContext } from './AuthContext';

interface NotificationContextValue {
  notifications: Notification[] | undefined;
  notificationsLoading: boolean;
  mutateNotifications: KeyedMutator<Notification[] | undefined>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const NotificationProvider = ({ children }: PropsWithChildren) => {
  const { get } = useClient();
  const { user, userLoading } = useAuthContext();

  const {
    data: notifications,
    isLoading,
    mutate: mutateNotifications,
  } = useSWR(user ? `/notifications/user/${user.id}` : null, get<Notification[]>, {
    refreshInterval: 20000, // Poll every 20 seconds
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        notificationsLoading: isLoading || !!userLoading,
        mutateNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// App renders every page inside NotificationProvider (App.tsx), so the value is always there.
export const useNotificationContext = () => useContext(NotificationContext) as NotificationContextValue;
